
    import HeadingWave from "@/features/shared/components/HeadingWave.vue";
    import Component from "vue-class-component";
    import Vue from "vue";
    import {
        ApplicationSigningState,
        ApplicationsListClient,
        ApplicationSortField,
        ApplicationState,
        ApplicationType,
        DocumentAttributesClient,
        CertificateDocumentTypeApiModel,
        Official,
        OfficialApplicationLightViewModel, OfficialsClient
    } from "@/api-client";
    import ApplicationStateBadge from "@/features/applications/components/ApplicationStateBadge.vue";
    import translateApplicationState from "@/features/applications/components/ApplicationStateTranslator";
    import translateApplicationSigningState from "@/features/applications/components/ApplicationSigningStateTranslator";
    import { BvTableCtxObject, BvTableFieldArray } from "bootstrap-vue";
    import { formatDate } from "@/formatting/dateFormatter";
    import DropdownSelector from "@/components/DropdownSelector.vue";
    import translateApplicationType from "@/features/applications/components/ApplicationTypeTranslator";
    import { Prop } from "vue-property-decorator";
    import TableEmptySlot from "@/features/shared/components/tables/TableEmptySlot.vue";
    import { translateTrueFalse } from "@/features/shared/TrueFalseTranslator";
    import Loader from "@/features/shared/components/Loader";
    import { debounceFn } from "debounce-decorator-ts";
    import PopoverInfo from '@/components/PopoverInfo.vue';
    import ServersidePaging from "@/components/ServersidePaging";
    import ServersidePagingSelector from "@/components/ServersidePagingSelector.vue";
    import FilterCheckbox from "@/components/FilterCheckbox.vue";

    class Filters {
        searchTerm: string;
        applicationType: ApplicationType | null;
        documentTypeCode: string;
        signingState: ApplicationSigningState | null;
        officialId: string | null;
        state: string;
        isDeleted!: boolean;

        constructor(searchTerm?: string, applicationType?: ApplicationType, documentTypeCode?: string, signingState?: ApplicationSigningState, officialId?: string, state?: string, isDeleted?:boolean) {
            this.searchTerm = searchTerm || "";
            this.applicationType = applicationType || null;
            this.documentTypeCode = documentTypeCode || "";
            this.signingState = signingState || null;
            this.officialId = officialId || "";
            this.state = state || "";
            this.isDeleted = isDeleted || false;
        }
    }

    @Component({
        components: {
            HeadingWave,
            FilterCheckbox,
            ApplicationStateBadge,
            DropdownSelector,
            TableEmptySlot,
            PopoverInfo,
            ServersidePagingSelector
        }
    })
    export default class ApplicationsTable extends Vue {
        @Prop({ default: () => false })
        onlyProcessing!: boolean;

        officials: Official[] = [new Official({ id: "", fullName: this.$ls.Applications_Table_Filters_Option_All })];

        documentTypes: CertificateDocumentTypeApiModel[] = [{ code: "", name: this.$ls.Applications_Table_Filters_Option_All } as CertificateDocumentTypeApiModel];
        applicationStates = [
            { value: [ApplicationState.ApplicationSubmitted, ApplicationState.PendingApplication, ApplicationState.DocumentIssued, ApplicationState.ApplicationRejected].join(","), name: this.$ls.Applications_Table_Filters_Option_All },
            { value: ApplicationState.ApplicationSubmitted, name: translateApplicationState(ApplicationState.ApplicationSubmitted) },
            { value: ApplicationState.PendingApplication, name: translateApplicationState(ApplicationState.PendingApplication) },
            { value: ApplicationState.DocumentIssued, name: translateApplicationState(ApplicationState.DocumentIssued) },
            { value: ApplicationState.ApplicationRejected, name: translateApplicationState(ApplicationState.ApplicationRejected) },
            { value: ApplicationState.ApplicationNotSubmitted, name: translateApplicationState(ApplicationState.ApplicationNotSubmitted) },
        ];
        processingApplicationStates = [
            { value: [ApplicationState.ApplicationSubmitted, ApplicationState.PendingApplication].join(","), name: this.$ls.Applications_Table_Filters_Option_All },
            { value: ApplicationState.ApplicationSubmitted, name: translateApplicationState(ApplicationState.ApplicationSubmitted) },
            { value: ApplicationState.PendingApplication, name: translateApplicationState(ApplicationState.PendingApplication) },
            { value: ApplicationState.ApplicationNotSubmitted, name: translateApplicationState(ApplicationState.ApplicationNotSubmitted) },
        ];
        applicationSigningStates = [
            { value: null, name: this.$ls.Applications_Table_Filters_Option_All },
            { value: ApplicationSigningState.NotSigned, name: translateApplicationSigningState(ApplicationSigningState.NotSigned) },
            { value: ApplicationSigningState.ForwardedToSigning, name: translateApplicationSigningState(ApplicationSigningState.ForwardedToSigning) },
            { value: ApplicationSigningState.Signed, name: translateApplicationSigningState(ApplicationSigningState.Signed) }
        ];
        applicationTypes = [
            { value: null, name: this.$ls.Applications_Table_Filters_Option_All },
            { value: ApplicationType.NewCertificate, name: translateApplicationType(ApplicationType.NewCertificate) },
            { value: ApplicationType.NewEndorsement, name: translateApplicationType(ApplicationType.NewEndorsement) },
            { value: ApplicationType.Duplicate, name: translateApplicationType(ApplicationType.Duplicate) }
        ];

        applications: OfficialApplicationLightViewModel[] = [];

        filters = new Filters();
        paging = new ServersidePaging();
        sort: ApplicationSortField = ApplicationSortField.DateSubmittedDesc;
        dataLoaded = false;
        loader = new Loader(true);

        async created() {
            this.getParametersFromRouteOrDefault();

            this.documentTypes = this.documentTypes.concat(await new DocumentAttributesClient().certificateDocumentTypes());
            this.officials = this.officials.concat(await new OfficialsClient().all());

            this.$watch(() => this.filters, () => {
                this.paging.pageNumber = 1;
                this.reflectParametersToRoute();
            }, { deep: true });

            this.$watch(() => this.paging.pageNumber, () => {
                this.reflectParametersToRoute();
            });

            this.$watch(() => this.paging.pageSize, () => {
                this.paging.pageNumber = 1;
                this.reflectParametersToRoute();
            });

            this.$watch(() => this.sort, () => {
                this.reflectParametersToRoute();
            });

            this.$watch(() => this.$route.query, () => {
                this.getApplications();
            }, { deep: true, immediate: true });
        }

        updateSort(ctx: BvTableCtxObject) {
            // Only supported sort option is "dateSubmitted".
            if (ctx.sortBy == "dateSubmitted")
                this.sort = ctx.sortDesc ? ApplicationSortField.DateSubmittedDesc : ApplicationSortField.DateSubmittedAsc;
        }

        async getApplications() {
            this.applications = [];
            const result = await new ApplicationsListClient().setLoader(this.loader).official(
                this.filters.searchTerm,
                this.filters.documentTypeCode,
                this.filters.state.split(",").filter(s => s != "") as ApplicationState[],
                this.filters.signingState,
                this.filters.officialId || null,
                this.filters.applicationType,
                this.paging.pageNumber,
                this.paging.pageSize,
                this.filters.isDeleted,
                this.sort,
            );
            this.paging.totalCount = result.totalCount;
            this.paging.pageSize = result.pageSize;
            this.applications = result.pageResult;
            this.dataLoaded = true;
        }

        @debounceFn(100) // Prevents possible double-update
        reflectParametersToRoute(): void {
            this.$router.replace({
                query: {
                    // Setting to 'undefined' removes parameter from URL.
                    searchTerm: this.filters.searchTerm || undefined,
                    documentTypeCode: this.filters.documentTypeCode || undefined,
                    state: this.filters.state || undefined,
                    signingState: this.filters.signingState || undefined,
                    officialId: this.filters.officialId || undefined,
                    applicationType: this.filters.applicationType || undefined,
                    pageNumber: this.paging.pageNumber ? this.paging.pageNumber.toString() : undefined,
                    pageSize: this.paging.pageSize ? this.paging.pageSize.toString() : undefined,
                    sort: this.sort || undefined,
                    isDeleted: this.filters.isDeleted.toString()
                }
            });
        }

        getParametersFromRouteOrDefault() {
            const route = this.$route as any;
            const searchTerm: string | undefined = route.query.searchTerm;
            const documentTypeCode: string | undefined = route.query.documentTypeCode;
            const signingState: ApplicationSigningState | undefined = route.query.signingState;
            const officialId: string | undefined = route.query.officialId;
            const applicationType: ApplicationType | undefined = route.query.applicationType;
            const state = route.query.state || (this.onlyProcessing
                ? this.processingApplicationStates[0].value
                : this.applicationStates[0].value);

            this.filters = new Filters(
                searchTerm,
                applicationType,
                documentTypeCode,
                signingState,
                officialId,
                state
            );

            this.paging.pageNumber = parseInt(route.query.pageNumber) || this.paging.pageNumber;
            this.paging.pageSize = parseInt(route.query.pageSize) || this.paging.pageSize;
            this.sort = route.query.sort || this.sort;
        }

        get isSortedByDescending(): boolean {
            return this.sort == ApplicationSortField.DateSubmittedDesc;
        }

        navigateToApplicationOverview(application: OfficialApplicationLightViewModel) {
            this.$router.push({ name: "ApplicationOverview", params: { applicationId: application.id } });
        }

        applicationFields: BvTableFieldArray = [
            { key: 'documentNumber', label: this.$ls.Applications_Table_Label_DocumentNumber, thClass: 'w-8' },
            { key: 'seafarerName', label: this.$ls.Applications_Table_Label_SeafarerName, thClass: 'w-10' },
            { key: 'certificateName', label: this.$ls.Global_Label_CertificateTypeName, thClass: 'w-25', tdClass: 'certificate-name' },
            { key: 'applicationType', label: this.$ls.Applications_Table_Label_ApplicationType, thClass: 'w-15', formatter: (value: any, key: any, item: OfficialApplicationLightViewModel): string => translateApplicationType(item.applicationType) },
            { key: 'dateSubmitted', label: this.$ls.Applications_Table_Label_DateSubmitted, sortable: true, formatter: (value: any, key: any, item: OfficialApplicationLightViewModel): string => formatDate(item.dateSubmitted), thClass: 'w-10' },
            { key: 'signingState', label: this.$ls.Applications_Table_Label_SigningState, thClass: 'w-8', formatter: (value: any, key: any, item: OfficialApplicationLightViewModel): string => translateApplicationSigningState(item.signingState) },
            { key: 'hasDocumentDeliveryBeenConfirmed', label: this.$ls.Applications_Table_Label_IsDocumentDelivered, thClass: 'w-10', formatter: (value: any, key: any, item: OfficialApplicationLightViewModel): string => translateTrueFalse(item.hasDocumentDeliveryBeenConfirmed) },
            { key: 'state', label: this.$ls.Applications_Table_Label_State, thClass: 'w-8', formatter: (state: ApplicationState): string => translateApplicationState(state) },
            { key: 'linkIcon', label: '', sortable: false, tdClass: "table-actions" }
        ];
    }
